import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Plus, Megaphone, MoreHorizontal, Copy, Archive, Trash2, Pause, Play, Clock, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import StatusPill from "@/components/StatusPill";
import { ChannelStatsMini } from "@/components/ChannelStatsModule";
import { getCampaignDisplay, getCampaignETA } from "@/lib/campaignStatus";
import {
    DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

function CampaignCard({ c, settings, onAction }) {
    const navigate = useNavigate();
    const channelLabel = c.channel === "both" ? "SMS + Email" : c.channel === "sms" ? "SMS" : "Email";
    const display = getCampaignDisplay(c, settings);
    const eta = getCampaignETA(c);
    const stats = c.channel_stats || {};
    const showSms = !!stats.sms;
    const showEmail = !!stats.email;
    const dual = showSms && showEmail;

    return (
        <div
            className="block bg-white border border-border rounded-xl p-5 hover:border-primary/40 hover:shadow-md transition-all cursor-pointer relative"
            onClick={() => navigate(`/campaigns/${c.id}`)}
            data-testid={`campaign-card-${c.id}`}
        >
            <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                    <div className="font-medium text-gray-900 truncate">{c.name}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">{channelLabel}</div>
                </div>
                <div className="flex items-center gap-1.5" onClick={e => e.stopPropagation()}>
                    <StatusPill tone={display.tone}>{display.label}</StatusPill>
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <button
                                className="p-1.5 rounded-md text-gray-400 hover:text-gray-700 hover:bg-gray-100"
                                aria-label="Campaign actions"
                                data-testid={`campaign-actions-${c.id}`}
                            >
                                <MoreHorizontal className="h-4 w-4" />
                            </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-44">
                            {c.status === "running" && (
                                <DropdownMenuItem onClick={() => onAction(c, "pause")} data-testid={`menu-pause-${c.id}`}>
                                    <Pause className="h-4 w-4 mr-2" /> Pause
                                </DropdownMenuItem>
                            )}
                            {c.status === "paused" && (
                                <DropdownMenuItem onClick={() => onAction(c, "resume")} data-testid={`menu-resume-${c.id}`}>
                                    <Play className="h-4 w-4 mr-2" /> Resume
                                </DropdownMenuItem>
                            )}
                            {(c.status === "paused" || c.status === "completed") && (
                                <DropdownMenuItem onClick={() => onAction(c, "restart")} data-testid={`menu-restart-${c.id}`}>
                                    <RotateCcw className="h-4 w-4 mr-2" /> Restart
                                </DropdownMenuItem>
                            )}
                            <DropdownMenuItem onClick={() => onAction(c, "duplicate")} data-testid={`menu-duplicate-${c.id}`}>
                                <Copy className="h-4 w-4 mr-2" /> Duplicate
                            </DropdownMenuItem>
                            {c.status !== "archived" && (
                                <DropdownMenuItem onClick={() => onAction(c, "archive")} data-testid={`menu-archive-${c.id}`}>
                                    <Archive className="h-4 w-4 mr-2" /> Archive
                                </DropdownMenuItem>
                            )}
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                                onClick={() => onAction(c, "delete")}
                                className="text-rose-700 focus:text-rose-700 focus:bg-rose-50"
                                data-testid={`menu-delete-${c.id}`}
                            >
                                <Trash2 className="h-4 w-4 mr-2" /> Delete
                            </DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>
                </div>
            </div>

            <div className={`mt-4 grid gap-3 ${dual ? "md:grid-cols-2 grid-cols-1" : "grid-cols-1"}`}>
                {showSms && <ChannelStatsMini channel="sms" stats={stats.sms} testid={`card-${c.id}-sms`} />}
                {showEmail && <ChannelStatsMini channel="email" stats={stats.email} testid={`card-${c.id}-email`} />}
            </div>

            <div className="mt-3 flex items-center gap-3 text-xs text-muted-foreground flex-wrap">
                <span>{c.send_rate_per_hour}/hr</span>
                {eta && (
                    <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" /> ETA {eta}</span>
                )}
                {c.last_activity_at && <span>last active {new Date(c.last_activity_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</span>}
            </div>
        </div>
    );
}

export default function Campaigns() {
    const { user } = useAuth();
    const [items, setItems] = useState([]);
    const [tab, setTab] = useState("all");
    const [confirm, setConfirm] = useState(null);
    const [settings, setSettings] = useState(null);

    const load = async () => {
        const params = tab !== "all" ? { status: tab } : {};
        const { data } = await api.get("/campaigns", { params });
        setItems(data);
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab]);
    useEffect(() => { api.get("/settings").then(r => setSettings(r.data)); }, []);

    const handleAction = (c, action) => {
        if (action === "pause" || action === "resume") {
            return runImmediate(c, action);
        }
        if (action === "duplicate") {
            return runImmediate(c, "duplicate");
        }
        if (action === "restart") {
            setConfirm({
                title: `Restart "${c.name}"?`,
                body: "Progress counters reset to zero and the campaign starts over. Past activity logs and message history are preserved.",
                destructive: false,
                confirmLabel: "Restart",
                run: () => runImmediate(c, "restart"),
            });
            return;
        }
        const verb = action === "delete" ? "Delete" : "Archive";
        setConfirm({
            title: `${verb} "${c.name}"?`,
            body: action === "delete"
                ? "This deletes the campaign record. Past activity and delivery analytics for this campaign remain in Activity for audit, but its dashboard card will disappear."
                : "Archived campaigns are hidden by default but kept for reporting. Stats are preserved.",
            destructive: action === "delete",
            confirmLabel: verb,
            run: () => runImmediate(c, action),
        });
    };

    const runImmediate = async (c, action) => {
        try {
            if (action === "delete") {
                await api.delete(`/campaigns/${c.id}`);
                toast.success("Campaign deleted");
            } else if (action === "duplicate") {
                const { data } = await api.post(`/campaigns/${c.id}/duplicate`);
                toast.success("Campaign duplicated");
                window.location.assign(`/campaigns/${data.id}`);
                return;
            } else {
                await api.post(`/campaigns/${c.id}/${action}`);
                toast.success(`Campaign ${action === "restart" ? "restarted" : action + "d"}`);
            }
            await load();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    return (
        <div className="space-y-6" data-testid="campaigns-page">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
                <div>
                    <h1 className="text-2xl md:text-3xl font-display font-semibold tracking-tight">Campaigns</h1>
                    <p className="text-sm text-muted-foreground mt-1">Manage outbound SMS and email campaigns.</p>
                </div>
                <Link to="/campaigns/new">
                    <Button size="sm" data-testid="new-campaign-button">
                        <Plus className="h-4 w-4 mr-1" /> New campaign
                    </Button>
                </Link>
            </div>

            <Tabs value={tab} onValueChange={setTab}>
                <TabsList data-testid="campaign-tabs" className="max-w-full overflow-x-auto justify-start">
                    <TabsTrigger value="all" data-testid="tab-all">All</TabsTrigger>
                    <TabsTrigger value="running" data-testid="tab-running">Running</TabsTrigger>
                    <TabsTrigger value="scheduled" data-testid="tab-scheduled">Scheduled</TabsTrigger>
                    <TabsTrigger value="paused" data-testid="tab-paused">Paused</TabsTrigger>
                    <TabsTrigger value="draft" data-testid="tab-draft">Drafts</TabsTrigger>
                    <TabsTrigger value="completed" data-testid="tab-completed">Completed</TabsTrigger>
                    <TabsTrigger value="archived" data-testid="tab-archived">Archived</TabsTrigger>
                    {user?.role === "admin" && (
                        <Link to="/admin/stale-send-jobs" data-testid="stale-send-jobs-link"
                              className="inline-flex items-center whitespace-nowrap rounded-md px-3 py-1 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground hover:bg-background/60">
                            Stale send jobs
                        </Link>
                    )}
                </TabsList>
            </Tabs>

            {items.length === 0 ? (
                <div className="bg-white border border-border rounded-xl p-12 text-center" data-testid="campaigns-empty">
                    <Megaphone className="h-8 w-8 mx-auto text-muted-foreground" strokeWidth={1.5} />
                    <div className="mt-3 text-base text-gray-900 font-medium">No campaigns here</div>
                    <div className="text-sm text-muted-foreground">Create one to start reaching your leads.</div>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {items.map(c => <CampaignCard key={c.id} c={c} settings={settings} onAction={handleAction} />)}
                </div>
            )}

            <AlertDialog open={!!confirm} onOpenChange={(o) => { if (!o) setConfirm(null); }}>
                <AlertDialogContent data-testid="campaign-confirm-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle>{confirm?.title}</AlertDialogTitle>
                        <AlertDialogDescription className="leading-relaxed">{confirm?.body}</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={async () => { const fn = confirm?.run; setConfirm(null); if (fn) await fn(); }}
                            className={confirm?.destructive ? "bg-rose-600 hover:bg-rose-700" : ""}
                            data-testid="campaign-confirm-action"
                        >
                            {confirm?.confirmLabel}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
